import { VoidCallback1 } from '@deqo/foundation';
import React, { useEffect, useRef } from 'react';
import useResizeObserver from 'use-resize-observer';
import confetti from '../../@vendor/confetti.js';
import { Communicator } from '../../concepts/objects';
import { toDegrees } from '../../platform/geom/Math';
import { Point } from '../../platform/geom/Point';
import { Fill } from '../../ui/Fill';
import { ContainerScope, resolve } from '../Container';
import { Flexer } from '../dashboard/elements/LayoutElements';
import { isItXmas } from '../occasions/xmas/WithXmas';
import { PreferencesModel } from '../preferences/PreferencesModel';
import { Colors } from './../../ui';
import { Camera } from './Camera';
import { ConfettiCannon, ConfettiFountain, ConfettiRound } from './ConfettiCannon';
import { Burdens } from './extensibility/Burdens';

export class ConfettiLayer extends React.Component {

    public static contextType = ContainerScope;

    private burdens: Burdens = new Burdens();
    private canvas: HTMLCanvasElement;
    private confetti: any;

    @resolve(Camera) camera!: Camera;
    @resolve(ConfettiCannon) cannon!: ConfettiCannon;
    @resolve(Communicator) comms!: Communicator;
    @resolve(PreferencesModel) preferences!: PreferencesModel;

    private get ready() {
        return !!this.canvas && !!this.confetti;
    }

    // private get mode(): 'default' | 'own' {
    //     return this.props.session.preferences.hideUsersEnabled ? 'own' : 'default';
    // }

    public render() {
        return <Fill id="confetti-layer" pointerEvents="none" zIndex="1">
            <ConfettiCanvas onInit={x => {
                this.canvas = x;
                this.confetti = confetti.create(x, { useWorker: false });
            }} />
        </Fill>;
    }

    public componentDidMount() {
        this.burdens.add(
            this.camera.observe(() => this.clearThenTransform),
            this.cannon.on('fire', this.onFire),
            this.cannon.on('ignite', this.onIgnite),
            this.comms.recv('confetti', (client, [a, b]) => {
                this.cannon.fire([client.id, Point.from(a), Point.from(b)]);
            }),
        );
    }

    public componentWillUnmount() {
        this.canvas = null;
    }

    private onFire = ([_userId, mpStart, mpEnd]: ConfettiRound) => {
        if (!this.ready) return;
        const { camera } = this;

        const mv = mpEnd.subtract(mpStart);
        const xy = mpStart.divide([camera.viewport.width, camera.viewport.height]);

        // Invert the x to be compatible with confetti function
        const angle = toDegrees(new Point(mv.x * -1, mv.y).angle());
        const velocity = ConfettiRound.computeVelocity(mv);

        const count = Math.round(velocity) * 3 / 7;
        const colors = isItXmas() && !this.preferences.scrooge2024
            ? ['#29a583', '#c9040b', '#28560b', '#fc1934', '#c09209',]
            : undefined;

        for (let i = -3; i < 4; i++) {
            this.confetti({
                canvas: this.canvas,
                colors,
                angle: angle + (5 * i),
                spread: 40,
                particleCount: count,
                origin: xy,
                startVelocity: velocity + 30 + (Math.abs(i) * -10),
                predraw: this.clearThenTransform,
            });
        }
    };

    private onIgnite = ([at, duration]: ConfettiFountain) => {
        if (!this.ready) return;
        const { camera } = this;

        const xy = at.divide([camera.viewport.width, camera.viewport.height]);
        const colors = [Colors.primary, Colors.positive];
        const end = Date.now() + (duration * 1000);
        const frame = () => {
            if (!this.ready) return;

            for (let i = 0; i < 2; i++) {
                this.confetti({
                    canvas: this.canvas,
                    particleCount: 2,
                    angle: 60,
                    spread: 55,
                    origin: xy,
                    colors,
                    predraw: this.clearThenTransform,
                });
                this.confetti({
                    canvas: this.canvas,
                    particleCount: 2,
                    angle: 120,
                    spread: 55,
                    origin: xy,
                    colors,
                    predraw: this.clearThenTransform,
                });
            }

            if (Date.now() < end) {
                setTimeout(frame, 30);
            }
        };

        frame();

    };


    private clearThenTransform = (canvas?: HTMLCanvasElement, gfx?: CanvasRenderingContext2D) => {
        const { camera } = this;
        canvas = canvas ?? this.canvas;
        gfx = gfx ?? canvas.getContext('2d');
        gfx.setTransform(1, 0, 0, 1, 0, 0);
        gfx.clearRect(0, 0, canvas.width, canvas.height);
        gfx.setTransform(camera.modelViewMatrix);
    };
}

const ConfettiCanvas = (p: { onInit?: VoidCallback1<HTMLCanvasElement> }) => {

    const layer = useRef<HTMLDivElement>();
    const canvas = useRef<HTMLCanvasElement>();

    const applySize = () => {
        if (canvas.current && layer.current) {
            canvas.current.width = layer.current.clientWidth;
            canvas.current.height = layer.current.clientHeight;
            return true;
        }
        return false;
    };

    useResizeObserver({
        ref: layer,
        onResize: applySize
    });

    useEffect(() => {
        if (applySize()) p.onInit(canvas.current);
    }, [p, layer.current, canvas.current]);

    return <Flexer id="confetti-layer" ref={layer} inset pointerOff zIndex="1">
        <canvas id="confett-canvas" ref={canvas}></canvas>
    </Flexer>;
};

// class ConfettiCanvas extends React.Component<> {

//     private canvas: HTMLCanvasElement;

//     public render() {
//         return <>
//             <canvas id="confett-canvas" ref={x => this.canvas = x}></canvas>
//             <Listener target={window} event="resize" callback={this.place} />
//         </>;
//     }

//     public componentDidMount() {
//         this.place();
//         const cx = this.props.onInit;
//         if (cx) cx(this.canvas);
//     }

//     public componentWillUnmount() {
//         const cx = this.props.onDestroy;
//         if (cx) cx();
//     }

//     public shouldComponentUpdate = () => false;

//     private place = () => {
//         const { canvas } = this;
//         if (!canvas) return;
//         canvas.width = canvas.parentElement.clientWidth;
//         canvas.height = canvas.parentElement.clientHeight;
//     };
// }
